import { motion } from "framer-motion";
import { ArrowUpRight, Music2, Youtube, Radio, Sparkles } from "lucide-react";

import { RELEASES, SPOTIFY_ARTIST_EMBED, ARTIST } from "@/lib/constants";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

// Small SVG service icons (monochrome gold)
const AppleMusicIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M23.994 6.124a9.23 9.23 0 0 0-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 0 0-1.877-.726 10.496 10.496 0 0 0-1.564-.15c-.04-.003-.083-.01-.124-.014H5.986c-.152.01-.303.017-.455.026a10.4 10.4 0 0 0-1.353.14c-.947.169-1.83.502-2.6 1.088A5.394 5.394 0 0 0 .17 4.65a9.13 9.13 0 0 0-.17 1.796v11.108c.004.056.01.111.013.166.02.577.078 1.152.187 1.718.353 1.812 1.253 3.202 2.9 4.06.577.301 1.2.483 1.834.588.632.104 1.27.15 1.91.157h11.192c.634-.005 1.267-.043 1.895-.147.858-.14 1.68-.402 2.42-.895.996-.663 1.664-1.6 2.019-2.759.202-.66.297-1.35.312-2.04.007-.325.015-.65.015-.976V6.124Zm-6.605 8.512c-.09.454-.34.85-.75 1.06-.436.223-.905.301-1.383.24-.482-.06-.85-.31-1.014-.752a1.184 1.184 0 0 1 .157-1.184c.14-.216.336-.396.535-.53.207-.138.436-.234.678-.286l1.176-.24 1.62-.336c.028-.005.056-.014.11-.028v-.126c0-.646.007-1.293-.002-1.939-.006-.502-.146-.997-.435-1.42-.291-.427-.696-.686-1.19-.788a2.44 2.44 0 0 0-.483-.05c-.98-.005-1.96-.001-2.938-.001v6.36c0 .21-.011.419-.06.626-.156.678-.657 1.099-1.34 1.163-.483.045-.947-.037-1.36-.31-.35-.234-.567-.573-.618-.999a1.203 1.203 0 0 1 .186-.833c.156-.24.393-.408.657-.514.226-.09.463-.135.706-.163l1.42-.244 1.325-.223v-6.68c0-.35.14-.62.464-.746a.807.807 0 0 1 .224-.06c1.31-.196 2.62-.395 3.93-.585.322-.047.643-.079.968-.075.395.006.786.088 1.14.28.598.324.94.87 1.048 1.542.033.208.05.42.05.63v6.986a.94.94 0 0 1-.017.19Z"/>
  </svg>
);

const BeatportIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0Zm-1.176 4.5h1.99v6.1l3.62-2.09.995 1.725-4.615 2.665v.15l4.616 2.664-.996 1.725-3.62-2.09v6.1h-1.99v-6.1L7.204 17.44l-.995-1.725 4.615-2.665v-.15L6.209 10.235l.996-1.725 3.619 2.09V4.5Z"/>
  </svg>
);

const SpotifyIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0Zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02Zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2Zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.42-1.02.599-1.559.3Z"/>
  </svg>
);

const platformButtons = (r) => [
  {
    id: "spotify",
    label: "Spotify",
    href: r.spotifyUrl,
    icon: <SpotifyIcon />,
  },
  {
    id: "apple",
    label: "Apple",
    href: r.appleUrl,
    icon: <AppleMusicIcon />,
  },
  {
    id: "beatport",
    label: "Beatport",
    href: r.beatportUrl,
    icon: <BeatportIcon />,
  },
  {
    id: "youtube",
    label: "YouTube",
    href: r.youtubeUrl,
    icon: <Youtube size={12} />,
  },
];

const CoverPlaceholder = ({ title }) => (
  <div
    aria-hidden
    className="absolute inset-0 flex items-center justify-center"
    style={{
      background:
        "linear-gradient(135deg, rgba(201,169,97,0.10) 0%, rgba(20,15,8,1) 45%, rgba(0,0,0,1) 100%)",
    }}
  >
    <div className="relative">
      <div className="absolute -inset-6 rounded-full border border-[color:var(--gold)]/20 float-soft" />
      <div className="absolute -inset-3 rounded-full border border-[color:var(--gold)]/40 float-soft" />
      <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--gold)]">
        <Sparkles size={20} className="text-[#0a0a0a]" />
      </div>
    </div>
    <span className="pointer-events-none absolute bottom-4 left-4 right-4 truncate font-editorial text-xs text-ink/60">
      {title}
    </span>
  </div>
);

const ReleaseCard = ({ r, i }) => {
  const isOut = r.status === "out";
  return (
    <motion.article
      variants={fadeUp}
      data-testid={`release-card-${i}`}
      className="group relative overflow-hidden rounded-sm border border-[color:var(--line)] bg-bg-2 transition-all duration-700 hover:border-[color:var(--gold)]/50"
    >
      {/* Artwork */}
      <div className="relative aspect-square w-full overflow-hidden">
        {r.cover ? (
          <img
            src={r.cover}
            alt={r.title}
            className="h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105"
          />
        ) : (
          <CoverPlaceholder title={r.title} />
        )}
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/20 to-transparent" />

        {/* Status badge */}
        <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full border border-[color:var(--gold)]/40 bg-[#0a0a0a]/70 px-3 py-1 backdrop-blur">
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              isOut
                ? "bg-[color:var(--gold-hi)] shadow-[0_0_10px_rgba(229,204,148,0.9)]"
                : "bg-white/50"
            }`}
          />
          <span className="text-[9px] font-bold uppercase tracking-[0.28em] text-ink/90">
            {isOut ? "Out Now" : "Coming Soon"}
          </span>
        </div>

        {/* Corner index */}
        <span className="absolute right-3 top-3 font-editorial text-lg text-[color:var(--gold-hi)]/80">
          0{i + 1}
        </span>
      </div>

      {/* Meta */}
      <div className="relative p-6 md:p-7">
        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">
          {r.genre}
        </p>
        <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold uppercase tracking-tight text-ink">
          {r.title}
        </h3>
        <p className="mt-1 font-editorial text-base text-[color:var(--gold-hi)]/80">
          {r.subtitle}
        </p>
        <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-muted">
          {r.label} · {r.year}
        </p>

        {/* Platforms */}
        <div className="mt-6 flex flex-wrap gap-2">
          {platformButtons(r).map((p) =>
            p.href ? (
              <a
                key={p.id}
                data-testid={`release-${i}-${p.id}`}
                href={p.href}
                target="_blank"
                rel="noreferrer noopener"
                className="group/btn inline-flex items-center gap-2 rounded-full border border-[color:var(--line-strong)] bg-transparent px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-ink/80 transition-all duration-500 hover:bg-[color:var(--gold)] hover:border-[color:var(--gold)] hover:text-[#0a0a0a]"
              >
                <span className="text-gold group-hover/btn:text-[#0a0a0a] transition-colors">
                  {p.icon}
                </span>
                {p.label}
              </a>
            ) : (
              <span
                key={p.id}
                data-testid={`release-${i}-${p.id}-disabled`}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-[color:var(--line)] bg-transparent px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-muted/60"
              >
                <span className="text-muted/50">{p.icon}</span>
                {p.label}
              </span>
            )
          )}
        </div>
      </div>
    </motion.article>
  );
};

export const Releases = () => {
  return (
    <section
      id="releases"
      data-testid="releases-section"
      className="relative w-full bg-bg py-24 md:py-32 border-t border-[color:var(--line)]"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10">
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold">
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              Chapter 07 — Discography
            </p>
            <h2
              data-testid="releases-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ink"
            >
              Featured{" "}
              <span className="font-editorial text-gold-hi">releases</span>
            </h2>
          </motion.div>
          <motion.a
            variants={fadeUp}
            data-testid="releases-spotify-link"
            href={ARTIST.spotifyArtistUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="group inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-ink/80 hover:text-[color:var(--gold-hi)] transition"
          >
            Full catalog on Spotify
            <ArrowUpRight
              size={14}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </motion.a>
        </motion.div>

        <motion.div
          variants={stagger(0.12, 0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
        >
          {RELEASES.map((r, i) => (
            <ReleaseCard key={r.title} r={r} i={i} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
